// ArchetypePlaybookEditor.js — inline JSON-safe editor for a drafted playbook.
// Renders each section (opening_line, checklist[], discovery_questions[],
// objection_handling[], close_or_next_step, disc_notes{}, do_not_say[]) as a
// dedicated control so admins don't need to hand-edit JSON. PATCHes the
// backend on save; parent reloads the list to pick up the new version.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Save, X, Plus, Trash2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _clone = (v) => JSON.parse(JSON.stringify(v));

const ArchetypePlaybookEditor = ({ playbook, domainId, headers, onSaved, onCancel }) => {
  const [draft, setDraft] = useState(() => _clone(playbook.sections));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setDraft(_clone(playbook.sections)); }, [playbook]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(playbook.sections), [draft, playbook.sections]);

  const setField = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const setListItem = (key, idx, val) => setDraft((d) => {
    const arr = [...(d[key] || [])];
    arr[idx] = val;
    return { ...d, [key]: arr };
  });
  const addListItem = (key, blank = '') => setDraft((d) => ({ ...d, [key]: [...(d[key] || []), blank] }));
  const removeListItem = (key, idx) => setDraft((d) => {
    const arr = [...(d[key] || [])];
    arr.splice(idx, 1);
    return { ...d, [key]: arr };
  });
  const setObjection = (idx, field, val) => setDraft((d) => {
    const arr = [...(d.objection_handling || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...d, objection_handling: arr };
  });
  const setDisc = (disc, val) => setDraft((d) => ({ ...d, disc_notes: { ...(d.disc_notes || {}), [disc]: val } }));

  const save = useCallback(async () => {
    setSaving(true); setErr('');
    try {
      await axios.patch(
        `${API}/admin/tfvf/v1_2/archetype-playbooks/${playbook.archetype}`,
        { domain_id: domainId, sections: draft },
        { headers },
      );
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }, [draft, domainId, headers, playbook.archetype, onSaved]);

  const inputClass = "w-full bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
  const smallInputClass = inputClass;

  return (
    <div className="mt-4 space-y-5 border-t border-white/10 pt-4" data-testid={`archetype-editor-${playbook.archetype}`}>
      {err && (
        <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-200">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{err}
        </div>
      )}

      <EditorSection title="Opening line">
        <input
          type="text"
          value={draft.opening_line || ''}
          onChange={(e) => setField('opening_line', e.target.value)}
          className={inputClass}
          data-testid="editor-opening-line"
        />
      </EditorSection>

      <EditorSection title="Checklist" onAdd={() => addListItem('checklist', 'New step')}>
        {(draft.checklist || []).map((step, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="text-white/40 text-xs pt-2.5 w-5 shrink-0">{i + 1}.</span>
            <input value={step} onChange={(e) => setListItem('checklist', i, e.target.value)} className={smallInputClass} />
            <button onClick={() => removeListItem('checklist', i)} className="p-2 text-rose-300 hover:bg-rose-500/10 rounded shrink-0" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </EditorSection>

      <EditorSection title="Discovery questions" onAdd={() => addListItem('discovery_questions', 'New question')}>
        {(draft.discovery_questions || []).map((q, i) => (
          <div key={i} className="flex gap-2">
            <input value={q} onChange={(e) => setListItem('discovery_questions', i, e.target.value)} className={smallInputClass} />
            <button onClick={() => removeListItem('discovery_questions', i)} className="p-2 text-rose-300 hover:bg-rose-500/10 rounded shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </EditorSection>

      <EditorSection title="Objection handling" onAdd={() => setDraft((d) => ({ ...d, objection_handling: [...(d.objection_handling || []), { objection: '', response: '' }] }))}>
        {(draft.objection_handling || []).map((o, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-2.5 space-y-2 relative">
            <button onClick={() => removeListItem('objection_handling', i)} className="absolute top-2 right-2 p-1 text-rose-300 hover:bg-rose-500/10 rounded"><X className="w-3 h-3" /></button>
            <div>
              <div className="text-white/50 text-[10px] uppercase tracking-wider mb-1">Objection</div>
              <input value={o.objection || ''} onChange={(e) => setObjection(i, 'objection', e.target.value)} className={smallInputClass} />
            </div>
            <div>
              <div className="text-white/50 text-[10px] uppercase tracking-wider mb-1">Response</div>
              <textarea value={o.response || ''} onChange={(e) => setObjection(i, 'response', e.target.value)} className={smallInputClass + ' min-h-[60px]'} />
            </div>
          </div>
        ))}
      </EditorSection>

      <EditorSection title="Close / next step">
        <textarea
          value={draft.close_or_next_step || ''}
          onChange={(e) => setField('close_or_next_step', e.target.value)}
          className={inputClass + ' min-h-[60px]'}
        />
      </EditorSection>

      <EditorSection title="DISC tone notes">
        <div className="grid md:grid-cols-2 gap-2">
          {['D', 'I', 'S', 'C'].map((disc) => (
            <div key={disc} className="bg-white/5 border border-white/10 rounded-lg p-2.5">
              <div className="text-teal-300 font-mono text-xs mb-1">{disc}</div>
              <textarea value={(draft.disc_notes || {})[disc] || ''} onChange={(e) => setDisc(disc, e.target.value)} className={smallInputClass + ' min-h-[50px]'} />
            </div>
          ))}
        </div>
      </EditorSection>

      <EditorSection title="Do NOT say" onAdd={() => addListItem('do_not_say', 'New anti-pattern')}>
        {(draft.do_not_say || []).map((s, i) => (
          <div key={i} className="flex gap-2">
            <input value={s} onChange={(e) => setListItem('do_not_say', i, e.target.value)} className={smallInputClass} />
            <button onClick={() => removeListItem('do_not_say', i)} className="p-2 text-rose-300 hover:bg-rose-500/10 rounded shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </EditorSection>

      <div className="flex items-center gap-2 pt-2 border-t border-white/10">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white font-semibold rounded-lg text-sm"
          data-testid="editor-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save changes {dirty ? '' : '(no changes)'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-lg text-sm"
          data-testid="editor-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const EditorSection = ({ title, onAdd, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <h4 className="text-white/50 text-[10px] uppercase tracking-widest font-semibold">{title}</h4>
      {onAdd && (
        <button onClick={onAdd} className="inline-flex items-center gap-1 text-xs text-teal-300 hover:text-teal-200">
          <Plus className="w-3 h-3" /> Add
        </button>
      )}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

export default ArchetypePlaybookEditor;
